import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransactions, useUI } from '../hooks';
import Text from '../components/Text/Text';
import TransactionItem from '../components/TransactionItem/TransactionItem';
import CreateDisputeForm from '../components/CreateDisputeForm/CreateDisputeForm';
import { Transaction } from '../types';

const TransactionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    transactions,
    getTransactions,
    releasePayment,
    isLoading,
    error,
    pagination
  } = useTransactions();
  const { showToast } = useUI();
  const [sortCriteria, setSortCriteria] = useState<'date' | 'amount'>('date');
  const [loadingMore, setLoadingMore] = useState(false);
  const [disputedTransactions, setDisputedTransactions] = useState<Transaction[]>([]);
  const [showDisputeForm, setShowDisputeForm] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  
  // Use a ref to track if transactions have been fetched
  const hasLoadedTransactions = useRef(false);
  
  // Fetch transactions only once on component mount
  useEffect(() => {
    // Only fetch if we haven't already loaded transactions
    if (!hasLoadedTransactions.current) {
      const fetchTransactions = async () => {
        try {
          await getTransactions(1, 10);
          // Mark as loaded
          hasLoadedTransactions.current = true;
        } catch (error) {
          showToast('error', 'Failed to load transactions');
        }
      };
      
      fetchTransactions();
    }
  }, [getTransactions, showToast]);
  
  // Handle transaction click
  const handleTransactionClick = useCallback((transaction: Transaction) => {
    navigate(`/payment/${transaction.id}`);
  }, [navigate]);
  
  // Handle release funds
  const handleReleaseFunds = useCallback(async (transaction: Transaction, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    navigate(`/release-funds/${transaction.id}`);
  }, [navigate]);
  
  // Handle provide tracking
  const handleProvideTracking = useCallback((transaction: Transaction, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    navigate(`/tracking/add/${transaction.id}`);
  }, [navigate]);
  
  // Handle open dispute
  const handleOpenDispute = useCallback((transaction: Transaction, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    setSelectedTransaction(transaction);
    setShowDisputeForm(true);
  }, []);
  
  // Handle dispute creation success
  const handleDisputeCreated = useCallback((disputeId: string) => {
    showToast('success', 'Dispute created successfully');
    setShowDisputeForm(false);
    navigate(`/disputes/${disputeId}`);
  }, [navigate, showToast]);
  
  // Handle load more
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !pagination.hasMore) return;
    
    setLoadingMore(true);
    try {
      await getTransactions(pagination.currentPage + 1, pagination.limit);
    } catch (error) {
      showToast('error', 'Failed to load more transactions');
    } finally {
      setLoadingMore(false);
    }
  }, [getTransactions, loadingMore, pagination.currentPage, pagination.hasMore, pagination.limit, setLoadingMore, showToast]);
  
  // Filter disputed transactions
  useEffect(() => {
    if (transactions) {
      setDisputedTransactions(transactions.filter(t => t.isDisputed));
    }
  }, [transactions]);
  
  // Sort transactions
  const sortedTransactions = transactions ? [...transactions].sort((a, b) => {
    if (sortCriteria === 'date') {
      return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
    } else {
      return b.totalAmount - a.totalAmount;
    }
  }) : [];
  
  // Render transaction item
  const renderTransactionItem = useCallback((transaction: Transaction) => {
    // Determine status text and color
    let statusText = '';
    let statusColor = '';
    
    if (transaction.status === 'disputed') {
      statusText = 'Disputed';
      statusColor = 'text-danger';
    } else if (transaction.status === 'in_escrow') {
      statusText = 'In Escrow';
      statusColor = 'text-primary';
    } else if (transaction.status === 'released') {
      statusText = 'Released';
      statusColor = 'text-success';
    } else if (transaction.status === 'pending') {
      statusText = 'Pending';
      statusColor = 'text-warning';
    } else if (transaction.status === 'refunded') {
      statusText = 'Refunded';
      statusColor = 'text-info';
    } else if (transaction.status === 'cancelled') {
      statusText = 'Cancelled';
      statusColor = 'text-danger';
    }
    
    // Determine if we can release funds
    const canReleaseFunds = transaction.status === 'in_escrow' && transaction.trackingId;
    
    // Determine if we need tracking
    const needsTracking = transaction.status === 'in_escrow' && !transaction.trackingId;
    
    // Create status element
    const statusElement = (
      <Text className={`text-caption ${statusColor}`}>
        {transaction.trackingId ? `${statusText} | Tracking Provided` : `${statusText} | No Tracking`}
      </Text>
    );
    
    // Create action element
    let actionElement = null;
    
    // if (canReleaseFunds) {
    //   actionElement = (
    //     <button
    //       className="release-funds-tag"
    //       onClick={(e) => handleReleaseFunds(transaction, e)}
    //     >
    //       <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
    //         <circle cx="12" cy="12" r="8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //         <path d="M12 8v8M9 12h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //         <path d="M16 7l4 -4M16 7h4M16 7v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //       </svg>
    //       Release Funds
    //     </button>
    //   );
    // } else if (needsTracking) {
    //   actionElement = (
    //     <button
    //       className="provide-tracking-tag"
    //       onClick={(e) => handleProvideTracking(transaction, e)}
    //     >
    //       <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
    //         <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //         <path d="M8 21V17m8 4v-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //         <path d="M12 10l-2 -2m2 2l2 -2m-2 2v5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //       </svg>
    //       Provide Tracking
    //     </button>
    //   );
    // }
    
    // Get buyer and seller public user IDs, falling back to internal IDs if needed
    const buyerPublicId = transaction.buyerPublicId
    const sellerPublicId = transaction.sellerPublicId;
    
    return (
      <TransactionItem
        key={transaction.id}
        role="buyer"
        itemDescription={`${transaction.itemDescription}`}
        itemPrice={`$${transaction.originalAmount?.toFixed(2) || '0.00'}`}
        itemBuyer={buyerPublicId}
        itemSeller={sellerPublicId}
        status={statusElement}
        action={actionElement}
        priceTagStyle="tag-left"
        onClick={() => handleTransactionClick(transaction)}
        onOpenDispute={handleOpenDispute}
        transaction={transaction}
      />
    );
  }, [handleReleaseFunds, handleProvideTracking, handleTransactionClick]);
  
  return (
    <div className="transactions-container">
      <div className="transactions-header">
        <div className="sort-controls">
          <Text className="text-body">Sort by:</Text>
          <div className="sort-buttons">
            <button
              className={`sort-button ${sortCriteria === 'date' ? 'active' : ''}`}
              onClick={() => setSortCriteria('date')}
            >
              Date
            </button>
            <button
              className={`sort-button ${sortCriteria === 'amount' ? 'active' : ''}`}
              onClick={() => setSortCriteria('amount')}
            >
              Amount
            </button>
          </div>
        </div>
      </div>
      
      <div className="transactions-list">
        {isLoading && !loadingMore ? (
          <div className="loading-state">
            <Text>Loading transactions...</Text>
          </div>
        ) : error ? (
          <div className="error-state">
            <Text className="text-error">Failed to load transactions</Text>
            <button
              className="retry-button"
              onClick={() => getTransactions(1, 10)}
            >
              Retry
            </button>
          </div>
        ) : sortedTransactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" stroke="#6c757d" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="9" y1="21" x2="9" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <Text className="text-body">No transactions yet</Text>
            <Text className="text-caption text-gray">
              When you make transactions, they will appear here
            </Text>
          </div>
        ) : (
          <>
            {/* Disputed Transactions Section (if any) */}
            {/* {disputedTransactions.length > 0 && (
              <div className="disputed-transactions-section">
                <h2>Disputed Transactions</h2>
                <div className="alert alert-warning">
                  <p>The following transactions are currently under dispute. Click on a transaction to view details.</p>
                </div>
                
                {disputedTransactions.map(transaction => (
                  <div key={transaction.id}>
                    {renderTransactionItem(transaction)}
                  </div>
                ))}
              </div>
            )} */}
            
            {/* Regular Transactions Section */}
            <h2>All Transactions</h2>
            {sortedTransactions.map(transaction => (
              <div key={transaction.id}>
                {renderTransactionItem(transaction)}
              </div>
            ))}
            
            {pagination.hasMore && (
              <button
                className="view-more-button"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Show More'}
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Dispute Form Modal */}
      {showDisputeForm && selectedTransaction && (
        <CreateDisputeForm
          transactionId={selectedTransaction.id}
          onClose={() => setShowDisputeForm(false)}
          onSuccess={handleDisputeCreated}
        />
      )}
    </div>
  );
};

export default TransactionsScreen;